// src/pages/update-profile/sub-admin-profile/index.jsx
import React, { useEffect, useMemo, useState } from "react";
import {
  Row,
  Col,
  Card,
  CardBody,
  Container,
  Form as RSForm,
  Input,
  FormFeedback,
  Label,
  Alert,
  Button
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import toastr from "toastr";
import withRouter from "../../../components/Common/withRouter";
import PhoneInput from "react-phone-input-2";
import "react-phone-input-2/lib/bootstrap.css";

import { useUserMe, useUpdateSubAdminProfile } from "../../../api/users";
import { mediaUrl } from "../../../helpers/mediaHelper";

const SubAdminProfile = () => {
  document.title = "Sub-Admin Profile | JobArk";

  const userMe = useUserMe();
  const updateProfile = useUpdateSubAdminProfile();

  const [photoFile, setPhotoFile] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);
  const [photoRemoved, setPhotoRemoved] = useState(false);

  // useEffect(() => {
  //   userMe.mutate();
  // }, []);

  const me = useMemo(() => {
    const raw = userMe?.data?.data ?? userMe?.data ?? {};
    return {
      email: raw.email || "",
      fullName: raw.fullName || "",
      phoneNumber: (raw.phoneNumber || "").replace(/^\+/, ""),
      photo: raw.photo || null
    };
  }, [userMe?.data]);

  // Set photo preview
  useEffect(() => {
    if (me.photo && !photoFile && !photoRemoved) {
      if (typeof me.photo === "string") {
        setPhotoPreview(mediaUrl(me.photo));
      } else if (Array.isArray(me.photo) && me.photo[0]) {
        setPhotoPreview(mediaUrl(me.photo[0]));
      }
    } else if (photoFile) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setPhotoPreview(reader.result);
      };
      reader.readAsDataURL(photoFile);
    } else if (photoRemoved) {
      setPhotoPreview(null);
    }
  }, [me.photo, photoFile, photoRemoved]);

  const validationSchema = Yup.object({
    fullName: Yup.string().required("Full Name is required"),
    phoneNumber: Yup.string().required("Phone Number is required")
  });

  const formik = useFormik({
    enableReinitialize: true,
    initialValues: {
      email: me.email,
      fullName: me.fullName,
      phoneNumber: me.phoneNumber
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const fd = new FormData();
        fd.append("fullName", values.fullName);

        // Normalize phone number (add + prefix)
        const normalizedPhone = values.phoneNumber
          ? `+${String(values.phoneNumber).replace(/^\+/, "")}`
          : "";
        fd.append("phoneNumber", normalizedPhone);

        // Append photo only if a new file was selected
        if (photoFile) {
          fd.append("photo", photoFile);
        } else if (photoRemoved) {
          // Send empty string to indicate photo removal
          fd.append("photo", "");
        }

        await updateProfile.mutateAsync(fd);
        toastr.success("Profile updated successfully");
        userMe.mutate();
        setPhotoFile(null);
        setPhotoRemoved(false);
      } catch (e) {
        // Error toast is handled by global axios interceptor
      } finally {
        setSubmitting(false);
      }
    }
  });

  const {
    values,
    errors,
    touched,
    handleChange,
    handleBlur,
    handleSubmit,
    setFieldValue,
    isSubmitting
  } = formik;

  const onRemovePhoto = () => {
    setPhotoFile(null);
    setPhotoPreview(null);
    setPhotoRemoved(true);
  };

  const handlePhotoChange = (e) => {
    const file = e.target.files?.[0] || null;
    if (!file) return;

    // Validate file type
    const allowedTypes = ["image/png", "image/jpeg", "image/jpg"];
    const fileExtension = file.name.split(".").pop()?.toLowerCase();
    if (!allowedTypes.includes(file.type) && !["png", "jpeg", "jpg"].includes(fileExtension)) {
      toastr.error("Please upload a PNG, JPEG, or JPG image");
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toastr.error("File size should be less than 5MB");
      return;
    }

    setPhotoFile(file);
    setPhotoRemoved(false);
  };

  const isLoading = updateProfile.isPending || isSubmitting;

  return (
    <div className="page-content">
      <Container fluid className="px-4">
        <Row className="justify-content-center">
          <Col md={8} lg={6}>
            {userMe.isError && (
              <Alert color="danger" className="mb-3">
                Failed to load profile data.
              </Alert>
            )}

            <Card>
              <CardBody>
                <h4 className="card-title mb-4 text-center">Update Profile</h4>

                {/* Photo Upload Section - Center */}
                <div className="d-flex justify-content-center mb-4">
                  <div className="position-relative" style={{ width: "150px", height: "150px" }}>
                    <div
                      style={{
                        width: "150px",
                        height: "150px",
                        borderRadius: "50%",
                        border: "3px solid #e0e0e0",
                        overflow: "hidden",
                        backgroundColor: "#f5f5f5",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        cursor: "pointer",
                        position: "relative"
                      }}
                      onClick={() => document.getElementById("photoInput").click()}
                    >
                      {photoPreview ? (
                        <img
                          src={photoPreview}
                          alt="Profile"
                          style={{
                            width: "100%",
                            height: "100%",
                            objectFit: "cover"
                          }}
                        />
                      ) : (
                        <div className="text-center p-3">
                          <i
                            className="mdi mdi-camera"
                            style={{ fontSize: "48px", color: "#999" }}
                          />
                          <div style={{ fontSize: "12px", color: "#999", marginTop: "8px" }}>
                            Upload Photo
                          </div>
                        </div>
                      )}
                    </div>
                    {photoPreview && (
                      <Button
                        type="button"
                        color="danger"
                        size="sm"
                        className="position-absolute"
                        style={{
                          top: "0",
                          right: "0",
                          borderRadius: "50%",
                          width: "30px",
                          height: "30px",
                          padding: "0",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center"
                        }}
                        onClick={(e) => {
                          e.stopPropagation();
                          onRemovePhoto();
                        }}
                      >
                        <i className="mdi mdi-close" style={{ fontSize: "16px" }} />
                      </Button>
                    )}
                    <input
                      id="photoInput"
                      type="file"
                      accept="image/png,image/jpeg,image/jpg"
                      className="d-none"
                      onChange={handlePhotoChange}
                    />
                  </div>
                </div>

                <RSForm onSubmit={handleSubmit}>
                  <div className="mb-3">
                    <Label className="form-label">Email</Label>
                    <Input
                      type="email"
                      name="email"
                      value={values.email || ""}
                      disabled
                      className="form-control-plaintext"
                      style={{
                        backgroundColor: "#f8f9fa",
                        padding: "0.375rem 0.75rem",
                        borderRadius: "0.25rem"
                      }}
                    />
                    <small className="text-muted">Email cannot be changed</small>
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">
                      Full Name <span className="text-danger">*</span>
                    </Label>
                    <Input
                      type="text"
                      name="fullName"
                      placeholder="Enter Full Name"
                      value={values.fullName || ""}
                      onChange={handleChange}
                      onBlur={handleBlur}
                      invalid={touched.fullName && !!errors.fullName}
                      disabled={isLoading}
                    />
                    {touched.fullName && errors.fullName && (
                      <FormFeedback type="invalid">{errors.fullName}</FormFeedback>
                    )}
                  </div>

                  <div className="mb-3">
                    <Label className="form-label">
                      Phone Number <span className="text-danger">*</span>
                    </Label>
                    <PhoneInput
                      country={"gb"}
                      enableSearch
                      containerClass="w-100"
                      inputProps={{
                        name: "phoneNumber"
                      }}
                      inputClass={
                        touched.phoneNumber && errors.phoneNumber
                          ? "form-control is-invalid"
                          : "form-control"
                      }
                      value={values.phoneNumber || ""}
                      onChange={(value) => setFieldValue("phoneNumber", value)}
                      onBlur={() => handleBlur({ target: { name: "phoneNumber" } })}
                      disabled={isLoading}
                    />
                    {touched.phoneNumber && errors.phoneNumber && (
                      <div className="invalid-feedback d-block">{errors.phoneNumber}</div>
                    )}
                  </div>

                  <div className="mt-4 d-grid">
                    <Button
                      type="submit"
                      color="primary"
                      disabled={isLoading}
                      className="btn-block"
                    >
                      {isLoading ? "Updating..." : "Update Profile"}
                    </Button>
                  </div>
                </RSForm>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </div>
  );
};

export default withRouter(SubAdminProfile);
